import { useState, useRef, useEffect } from 'react';
import {
  PlayIcon,
  EyeIcon,
  ClockIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import type { YouTubeVideo } from '@mermaids/shared';
import { formatViewCount, truncateTitle } from './utils';

interface YouTubeVideoCardProps {
  video: YouTubeVideo;
  index: number;
  onVideoClick: (videoId: string) => void;
}

const YouTubeVideoCard: React.FC<YouTubeVideoCardProps> = ({
  video,
  index,
  onVideoClick,
}) => {
  const [isInView, setIsInView] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-700 cursor-pointer overflow-hidden transform ${
        isInView ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      } ${isHovered ? 'scale-105 -rotate-1' : 'scale-100 rotate-0'}`}
      style={{
        transitionDelay: `${index * 0.1}s`,
        transformOrigin: 'center',
      }}
      onClick={() => onVideoClick(video.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      {/* Thumbnail Container */}
      <div className="relative overflow-hidden rounded-t-2xl">
        <div className="relative aspect-video">
          <img
            src={
              video.thumbnail.high ||
              video.thumbnail.medium ||
              video.thumbnail.default
            }
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-100 scale-75">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse hover:animate-none transform hover:scale-110 transition-all duration-300">
              <PlayIcon className="w-8 h-8 text-white ml-1" />
            </div>
          </div>

          {/* Duration badge */}
          <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm font-semibold flex items-center space-x-1 transform transition-all duration-300 group-hover:scale-110">
            <ClockIcon className="w-4 h-4" />
            <span>{video.duration}</span>
          </div>

          {/* View count badge */}
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-lg text-sm font-semibold flex items-center space-x-1 transform transition-all duration-300 group-hover:scale-110">
            <EyeIcon className="w-4 h-4" />
            <span>{formatViewCount(video.viewCount)}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 relative z-10">
        <h3 className="font-heading font-bold text-xl text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2 leading-tight">
          {truncateTitle(video.title)}
        </h3>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
          {video.description}
        </p>

        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-4 h-4" />
            <span className="font-medium">
              {format(new Date(video.publishedAt), 'MMM dd, yyyy')}
            </span>
          </div>

          <div className="text-right">
            <p className="font-semibold text-gray-700">{video.channelTitle}</p>
          </div>
        </div>
      </div>

      {/* Animated border */}
      <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:from-blue-500 group-hover:via-purple-500 group-hover:to-pink-500 transition-all duration-500"></div>

      {/* Shimmer effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
        <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
      </div>

      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full animate-float-particles`}
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + i * 10}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${2 + i * 0.5}s`,
            }}
          ></div>
        ))}
      </div>
    </div>
  );
};

export default YouTubeVideoCard;
